import cn from "classnames";
import Link from "next/link";
import { FC, ReactElement, useEffect, useRef, useState } from "react";

import { ChevronLeftIcon } from "@heroicons/react/outline";
import { ArrowLeftIcon } from "@heroicons/react/solid";
import CircleXMark from "@svg/circle-x-mark";
import SearchIcon from "@svg/search";
import HeaderMegaMenu from "./header-megamenu";
import { IMegamenuInput, ImegaMenuMainItem } from "features/home/helpers/types";

type FlatItem = { name: string; url: string };

interface Props {
  megamenu?: IMegamenuInput;
  onBack: () => void;
  onClose: () => void;
}
const MegaMenuSidebar: FC<Props> = ({
  megamenu,
  onBack,
  onClose,
}): ReactElement => {
  const [activeLevel, setActiveLevel] = useState<ImegaMenuMainItem>();
  const [searchResults, setSearchResults] = useState<FlatItem[]>([]);
  const [term, setTerm] = useState("");

  // to remove items without child and prepare flatten items for search
  const megamenuItems = useRef<ImegaMenuMainItem[]>([]);
  const megamenuFlattenItems = useRef<FlatItem[]>([]);

  useEffect(() => {
    if (!megamenu) return;
    megamenuItems.current = megamenu.data.filter((item) => item.children);

    megamenu.data.forEach((item) => {
      megamenuFlattenItems.current.push({ name: item.name, url: item.url });
      if (item.children) {
        item.children.forEach((child) => {
          megamenuFlattenItems.current.push({
            name: child.name,
            url: child.url,
          });
        });
      }
    });
  }, [megamenu]);

  // filter flatten items based on searched term
  useEffect(() => {
    if (megamenuFlattenItems.current.length === 0) return;
    if (!term) {
      setSearchResults([]);
      return;
    }
    setSearchResults(
      megamenuFlattenItems.current.filter((item) =>
        item.name.includes(term.trim())
      )
    );
  }, [term]);

  const renderBackButton = () => {
    if (activeLevel)
      return {
        text: activeLevel.name,
        onClick: () => setActiveLevel(undefined),
      };
    return {
      text: "دسته‌بندی‌ها",
      onClick: onBack,
    };
  };

  const renderItems = () => {
    if (term)
      return searchResults.map((item, index) => (
        <Link prefetch={false} href={item.url} key={index}>
          <button
            className={cn(
              "flex w-full justify-between border-zinc-200 py-5 text-right border-b",
              {
                "border-b": index + 1 !== searchResults?.length,
              },
              { "mt-1": index > 0 }
            )}
            key={index}
            onClick={onClose}
          >
            {item.name}
          </button>
        </Link>
      ));
    // level 1 items
    if (activeLevel)
      return activeLevel?.children?.map((item, index) => {
        return (
          <Link prefetch={false} href={item.url} key={index}>
            <button
              className={cn(
                "flex w-full items-center justify-between border-zinc-200 py-5 text-right",
                {
                  "border-b": index + 1 !== activeLevel?.children?.length,
                }
              )}
              onClick={onClose}
            >
              {item.name}
            </button>
          </Link>
        );
      });

    // main items
    return megamenuItems.current.map((item, index) => (
      <button
        className={cn(
          "flex w-full items-center justify-between border-zinc-200 py-5 text-right",
          {
            "border-b": index + 1 !== megamenuItems.current.length,
          }
        )}
        key={index}
        onClick={() => setActiveLevel(megamenuItems.current[index])}
      >
        {item.name}
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
    ));
  };

  return (
    <div className="h-full w-full overflow-y-auto">
      <button
        className="relative flex h-16 w-full items-center justify-center px-10 text-right text-[16px] font-medium leading-6 text-zinc-700 shadow-[0_1px_2px_rgba(0,0,0,.08)]"
        onClick={renderBackButton().onClick}
      >
        {renderBackButton().text}
        <ArrowLeftIcon className="absolute right-4 h-5 w-5 rotate-180" />
      </button>
      {/* search */}
      {!activeLevel && (
        <div className="relative m-4">
          <SearchIcon className="absolute bottom-1/2 right-3 h-4 w-4 translate-y-1/2 -scale-x-100 fill-zinc-600" />
          {term && (
            <CircleXMark
              className="absolute bottom-1/2 left-3 h-5 w-5 translate-y-1/2 -scale-x-100 cursor-pointer text-zinc-400"
              onClick={() => setTerm("")}
            />
          )}
          <input
            className="w-full rounded-lg bg-zinc-50 py-2.5 px-10 text-sm outline-none placeholder:text-zinc-500"
            placeholder="جستجوی دسته‌بندی"
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
            }}
          />
        </div>
      )}
      {/* search : end */}
      <HeaderMegaMenu activeLevel={activeLevel} onClose={onClose} />
      <div className="px-6">{renderItems()}</div>
    </div>
  );
};

export default MegaMenuSidebar;
